// Escenario 3D: renderizador, cámara, controles, luces, mesa de trabajo y postproceso.
// Solo dibuja cuando algo cambia (invalidate) para no gastar batería con la escena quieta.
import * as THREE from 'three';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import { RoomEnvironment } from 'three/addons/environments/RoomEnvironment.js';
import { EffectComposer } from 'three/addons/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/addons/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/addons/postprocessing/UnrealBloomPass.js';
import { OutputPass } from 'three/addons/postprocessing/OutputPass.js';

export const MAT_W = 640;
export const MAT_D = 440;

function matTexture() {
  const pxmm = 3.2;
  const c = document.createElement('canvas');
  c.width = Math.round(MAT_W * pxmm);
  c.height = Math.round(MAT_D * pxmm);
  const g = c.getContext('2d');
  g.fillStyle = '#1b3a30';
  g.fillRect(0, 0, c.width, c.height);
  // Textura sutil
  const img = g.getImageData(0, 0, c.width, c.height);
  for (let i = 0; i < img.data.length; i += 4) {
    const n = (Math.random() - 0.5) * 6;
    img.data[i] += n;
    img.data[i + 1] += n;
    img.data[i + 2] += n;
  }
  g.putImageData(img, 0, 0);
  const line = (x0, y0, x1, y1, w, color) => {
    g.strokeStyle = color;
    g.lineWidth = w;
    g.beginPath();
    g.moveTo(x0, y0);
    g.lineTo(x1, y1);
    g.stroke();
  };
  for (let mm = 0; mm <= MAT_W; mm += 10) {
    const major = mm % 50 === 0;
    line(mm * pxmm, 0, mm * pxmm, c.height, major ? 2 : 1, major ? 'rgba(170,215,190,0.38)' : 'rgba(150,200,175,0.16)');
  }
  for (let mm = 0; mm <= MAT_D; mm += 10) {
    const major = mm % 50 === 0;
    line(0, mm * pxmm, c.width, mm * pxmm, major ? 2 : 1, major ? 'rgba(170,215,190,0.38)' : 'rgba(150,200,175,0.16)');
  }
  // Diagonales de 45° como en las bases de corte
  g.save();
  g.globalAlpha = 0.1;
  line(0, c.height, c.height, 0, 2, '#e8d58a');
  line(c.width - c.height, c.height, c.width, 0, 2, '#e8d58a');
  g.restore();
  g.fillStyle = 'rgba(200,230,210,0.5)';
  g.font = `${Math.round(3.4 * pxmm)}px system-ui, sans-serif`;
  for (let mm = 50; mm < MAT_W; mm += 50) g.fillText(String(mm / 10), mm * pxmm + 4, 4.5 * pxmm);
  for (let mm = 50; mm < MAT_D; mm += 50) g.fillText(String(mm / 10), 4, mm * pxmm - 4);
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 8;
  return tex;
}

export class Stage {
  constructor(container) {
    this.container = container;
    this.needsRender = true;
    this.quality = 'alta';
    this.listeners = new Set();
    this.animating = new Set(); // funciones (dt) => boolean (true = sigue animando)

    const renderer = new THREE.WebGLRenderer({ antialias: true, powerPreference: 'high-performance', preserveDrawingBuffer: false });
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.05;
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFShadowMap;
    renderer.domElement.className = 'stage-canvas';
    container.appendChild(renderer.domElement);
    this.renderer = renderer;

    const scene = new THREE.Scene();
    scene.background = new THREE.Color('#151a20');
    scene.fog = new THREE.Fog('#151a20', 900, 2200);
    this.scene = scene;

    const pmrem = new THREE.PMREMGenerator(renderer);
    scene.environment = pmrem.fromScene(new RoomEnvironment(), 0.04).texture;
    scene.environmentIntensity = 0.45;

    const camera = new THREE.PerspectiveCamera(38, 1, 2, 6000);
    camera.position.set(0, 300, 270);
    this.camera = camera;

    const controls = new OrbitControls(camera, renderer.domElement);
    controls.target.set(0, 0, 20);
    controls.enableDamping = true;
    controls.dampingFactor = 0.12;
    controls.zoomToCursor = true;
    controls.minDistance = 25;
    controls.maxDistance = 1500;
    controls.maxPolarAngle = Math.PI * 0.49;
    controls.screenSpacePanning = false;
    controls.addEventListener('change', () => this.invalidate());
    this.controls = controls;

    // Luces
    const hemi = new THREE.HemisphereLight('#dfe8f2', '#30343b', 0.95);
    scene.add(hemi);
    const sun = new THREE.DirectionalLight('#fff4e8', 2.1);
    sun.position.set(-160, 420, 230);
    sun.castShadow = true;
    sun.shadow.mapSize.set(2048, 2048);
    const sc = sun.shadow.camera;
    sc.left = -340;
    sc.right = 340;
    sc.top = 260;
    sc.bottom = -260;
    sc.near = 50;
    sc.far = 1200;
    sun.shadow.bias = -0.0004;
    sun.shadow.normalBias = 0.25;
    sun.shadow.radius = 3;
    scene.add(sun);
    this.sun = sun;
    const fill = new THREE.DirectionalLight('#cfe0ff', 0.5);
    fill.position.set(220, 180, -200);
    scene.add(fill);

    // Mesa: base de corte con cuadrícula
    const matTex = matTexture();
    const mat = new THREE.Mesh(
      new THREE.BoxGeometry(MAT_W, 2, MAT_D),
      [
        new THREE.MeshStandardMaterial({ color: '#173329', roughness: 0.9 }),
        new THREE.MeshStandardMaterial({ color: '#173329', roughness: 0.9 }),
        new THREE.MeshStandardMaterial({ map: matTex, roughness: 0.88, metalness: 0 }),
        new THREE.MeshStandardMaterial({ color: '#173329', roughness: 0.9 }),
        new THREE.MeshStandardMaterial({ color: '#173329', roughness: 0.9 }),
        new THREE.MeshStandardMaterial({ color: '#173329', roughness: 0.9 }),
      ],
    );
    mat.position.y = -1;
    mat.receiveShadow = true;
    mat.userData.ground = true;
    scene.add(mat);
    this.mat = mat;
    const floor = new THREE.Mesh(new THREE.PlaneGeometry(6000, 6000), new THREE.MeshStandardMaterial({ color: '#1b2027', roughness: 1 }));
    floor.rotation.x = -Math.PI / 2;
    floor.position.y = -2.2;
    floor.receiveShadow = true;
    floor.userData.ground = true;
    scene.add(floor);

    this.world = new THREE.Group();
    scene.add(this.world);
    this.overlay = new THREE.Group(); // marcadores de pines, selección, cable en curso
    scene.add(this.overlay);

    // Postproceso: bloom para el brillo de LEDs y pantallas
    const size = new THREE.Vector2();
    renderer.getSize(size);
    const rt = new THREE.WebGLRenderTarget(1, 1, { samples: 4, type: THREE.HalfFloatType });
    this.composer = new EffectComposer(renderer, rt);
    this.composer.addPass(new RenderPass(scene, camera));
    // Umbral > 1: solo brilla lo emisivo (LEDs, pantallas), no las superficies blancas iluminadas.
    this.bloom = new UnrealBloomPass(new THREE.Vector2(256, 256), 0.75, 0.4, 2.4);
    this.composer.addPass(this.bloom);
    this.composer.addPass(new OutputPass());

    this.raycaster = new THREE.Raycaster();
    this.pointer = new THREE.Vector2();
    this.timer = new THREE.Timer();

    this.resizeObserver = new ResizeObserver(() => this.resize());
    this.resizeObserver.observe(container);
    this.resize();
    // Sin preferencia guardada: calidad media en celulares y equipos con pocos núcleos.
    const modest = (navigator.hardwareConcurrency || 8) <= 4 || matchMedia('(pointer: coarse)').matches;
    this.setQuality(localStorageGet('arduino3d.quality') || (modest ? 'media' : 'alta'));

    renderer.setAnimationLoop(() => this.tick());
  }

  setQuality(q) {
    this.quality = q;
    const dpr = window.devicePixelRatio || 1;
    const ratio = q === 'alta' ? Math.min(dpr, 2) : q === 'media' ? Math.min(dpr, 1.25) : 1;
    this.renderer.setPixelRatio(ratio);
    this.renderer.shadowMap.enabled = q !== 'baja';
    this.sun.castShadow = q !== 'baja';
    this.bloom.enabled = q !== 'baja';
    this.scene.traverse((o) => {
      if (o.material) {
        const mats = Array.isArray(o.material) ? o.material : [o.material];
        for (const m of mats) m.needsUpdate = true;
      }
    });
    this.resize();
    try {
      localStorage.setItem('arduino3d.quality', q);
    } catch {
      // sin almacenamiento local
    }
  }

  resize() {
    const w = Math.max(1, this.container.clientWidth);
    const h = Math.max(1, this.container.clientHeight);
    this.renderer.setSize(w, h, false);
    this.composer.setPixelRatio(this.renderer.getPixelRatio());
    this.composer.setSize(w, h);
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
    this.invalidate();
  }

  invalidate() {
    this.needsRender = true;
  }

  /** Registra una animación: fn(dt) devuelve true mientras deba seguir. */
  animate(fn) {
    this.animating.add(fn);
    this.invalidate();
  }

  tick() {
    this.timer.update();
    const dt = Math.min(0.1, this.timer.getDelta());
    if (this.animating.size) {
      for (const fn of this.animating) if (!fn(dt)) this.animating.delete(fn);
      this.needsRender = true;
    }
    this.controls.update();
    for (const fn of this.listeners) fn(dt);
    if (!this.needsRender) return;
    this.needsRender = false;
    if (this.quality === 'baja') this.renderer.render(this.scene, this.camera);
    else this.composer.render(dt);
  }

  onTick(fn) {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  // Selección por rayo ------------------------------------------------------------------------
  setPointer(clientX, clientY) {
    const r = this.renderer.domElement.getBoundingClientRect();
    this.pointer.set(((clientX - r.left) / r.width) * 2 - 1, -((clientY - r.top) / r.height) * 2 + 1);
    this.raycaster.setFromCamera(this.pointer, this.camera);
    return this.raycaster;
  }

  raycast(clientX, clientY, objects = [this.world], recursive = true) {
    this.setPointer(clientX, clientY);
    return this.raycaster.intersectObjects(objects, recursive);
  }

  /** Punto del plano horizontal y = h bajo el cursor. */
  groundPoint(clientX, clientY, h = 0) {
    this.setPointer(clientX, clientY);
    const plane = new THREE.Plane(new THREE.Vector3(0, 1, 0), -h);
    const p = new THREE.Vector3();
    return this.raycaster.ray.intersectPlane(plane, p) ? p : null;
  }

  /** Coordenadas de pantalla (px, relativas al canvas) de un punto del mundo. */
  toScreen(v, out = { x: 0, y: 0, z: 0 }) {
    const p = _v.copy(v).project(this.camera);
    const r = this.renderer.domElement;
    out.x = ((p.x + 1) / 2) * r.clientWidth;
    out.y = ((1 - p.y) / 2) * r.clientHeight;
    out.z = p.z;
    return out;
  }

  // Vistas de cámara ---------------------------------------------------------------------------
  flyTo(position, target, ms = 450) {
    const p0 = this.camera.position.clone();
    const t0 = this.controls.target.clone();
    let t = 0;
    this.animate((dt) => {
      t = Math.min(1, t + (dt * 1000) / ms);
      const e = t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
      this.camera.position.lerpVectors(p0, position, e);
      this.controls.target.lerpVectors(t0, target, e);
      return t < 1;
    });
  }

  /** Encuadra la caja dada. top = vista desde arriba. */
  frameBox(box, { top = false } = {}) {
    if (box.isEmpty()) box = new THREE.Box3(new THREE.Vector3(-80, 0, -60), new THREE.Vector3(80, 20, 60));
    const center = box.getCenter(new THREE.Vector3());
    const size = box.getSize(new THREE.Vector3());
    const vfov = (this.camera.fov * Math.PI) / 180;
    const hfov = 2 * Math.atan(Math.tan(vfov / 2) * this.camera.aspect);
    const target = new THREE.Vector3(center.x, top ? 0 : center.y * 0.3, center.z);
    let dir;
    let halfW;
    let halfH;
    if (top) {
      dir = new THREE.Vector3(0, 1, 0.0001);
      halfW = size.x / 2;
      halfH = size.z / 2;
    } else {
      dir = new THREE.Vector3(0, 0.82, 0.57).normalize();
      halfW = size.x / 2;
      // Profundidad vista en escorzo más la altura de las piezas
      halfH = (size.z * dir.y + size.y * dir.z) / 2;
    }
    const margin = 1.32; // deja lugar a la barra de herramientas flotante
    const dist = Math.max(halfW / Math.tan(hfov / 2), halfH / Math.tan(vfov / 2), 60) * margin + size.y * 0.5;
    this.flyTo(target.clone().addScaledVector(dir, dist), target);
  }

  dispose() {
    this.renderer.setAnimationLoop(null);
    this.resizeObserver.disconnect();
    this.renderer.dispose();
  }
}

const _v = new THREE.Vector3();

function localStorageGet(k) {
  try {
    return localStorage.getItem(k);
  } catch {
    return null;
  }
}
